import React, { memo, useCallback, ForwardedRef } from 'react';
import trim from 'lodash/trim';

import { ClassName, GADataType } from '../../../types';
import { IconsEnum } from '../../../assets/icons/types';

import { useTranslate } from '../../../common/hooks/useTranslate';
import { FiltersProps, useFilters } from '../hooks/useFilters';

import { Icon } from '../../Icon';

interface TextFilterProps extends FiltersProps<string> {
  autoComplete?: string;
  changeOnEnter?: boolean;
  className?: string;
  disabled?: boolean;
  i18nPlaceholder?: string;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  iconWrapperClassName?: ClassName;
  id?: string;
  inputClassName?: string;
  inputRef?: ForwardedRef<HTMLInputElement>;
  onFocus?: () => void;
  placeholder?: string;
  trimmedChars?: string;
  type?: string;
  wait?: number;
}

function TextFilter({
  autoComplete = 'off',
  changeOnEnter = false,
  className,
  dataGa,
  disabled,
  i18nPlaceholder,
  icon,
  iconClassName,
  iconWrapperClassName,
  id,
  inputClassName,
  inputRef,
  name,
  onChange,
  onFocus,
  placeholder,
  trimmedChars,
  type,
  value = '',
  wait = 500
}: TextFilterProps & GADataType) {
  const { t } = useTranslate();

  const { updatedValue, handleChange, handleKeyDown } = useFilters<string>({
    changeOnEnter,
    debounced: true,
    emptyValue: '',
    name,
    onChange,
    value,
    wait
  });

  const handleInputChange = useCallback(
    ({ target: { value: inputValue } }) => {
      handleChange(trimmedChars ? trim(inputValue, trimmedChars) : inputValue);
    },
    [trimmedChars, handleChange]
  );

  return (
    <div className={className}>
      <div
        className={
          iconWrapperClassName ||
          'absolute flex inset-y-0 items-center left-0 ml-3 pointer-events-none'
        }
      >
        <Icon icon={icon || IconsEnum.SEARCH} className={iconClassName} />
      </div>
      <input
        ref={inputRef}
        autoComplete={autoComplete}
        className={inputClassName}
        data-ga={dataGa}
        disabled={disabled}
        id={id}
        name={name}
        onChange={handleInputChange}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        placeholder={i18nPlaceholder ? t(i18nPlaceholder) : placeholder}
        type={type}
        value={updatedValue}
      />
    </div>
  );
}

export default memo(TextFilter);
